import { contentful } from 'api/contentful/api'
import { TArticleSetupInstance } from 'api/contentful/articles'
import { getArticle, IArticle } from 'api/contentful/getArticle'
import { getArticleIds } from 'api/contentful/getArticleIds'
import { useArticleList } from 'api/contentful/getArticleList'
import { ArticleContent } from 'components/articles/articleContent/ArticleContent'
import { ArticleContentTable } from 'components/articles/articleContentTable/ArticleContentTable'
import { Block } from 'components/Block'
import { Footer } from 'components/footer/Footer'
import { HeaderDashboard } from 'components/header/headerDashboard/HeaderDashboard'
import { TopNavigationLandingPage } from 'components/header/topNavigationLandingPage/TopNavigationLandingPage'
import { Layout } from 'components/layout/Layout'
import { PageMeta } from 'components/PageMeta'
import { PageTitle } from 'components/PageTitle'
import { ProfileContent, ProfileWrapper } from 'components/profile/profileContent/ProfileContent'
import { ProfileSidebar } from 'components/profile/profileSidebar/ProfileSidebar'
import { SideLayout } from 'components/sideLayout/SideLayout'
import { BlockSpinner } from 'components/spinner/Spinner'
import { GetStaticPaths, GetStaticProps } from 'next'
import { useTranslation } from 'next-i18next'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { Fragment, ReactNode, useMemo } from 'react'
import { useAuth } from 'utils/auth/hooks/useAuth'
import { REVALIDATE_SECS } from 'utils/constants'
import { getIntlProps } from 'utils/i18n/getIntlProps'

export default function Page(props: {
  article: IArticle | null
  articlesSetup?: TArticleSetupInstance
}) {
  const { t } = useTranslation()
  const { article, articlesSetup } = props
  const articlesMenu = articlesSetup?.endOfTheLivePlanningMenuCollection?.items
  const isInMenu = articlesMenu?.some((articleNode) =>
    articleNode?.nodesCollection?.items?.some(
      (articleItem) => articleItem?.article?.sys?.id === article?.sys?.id
    )
  )

  const router = useRouter()
  const isInside = router.query.inside as string
  const articleList = useArticleList()
  const { isLogged } = useAuth()

  const ConditionalLayout = ({ children }: { children: ReactNode }) =>
    isInside === 'true' && isLogged ? (
      <SideLayout left={<ProfileSidebar />} header={<HeaderDashboard />}>
        <Block>
          <ProfileWrapper>
            <ProfileContent>{children}</ProfileContent>
          </ProfileWrapper>
        </Block>
      </SideLayout>
    ) : (
      <Layout
        header={<TopNavigationLandingPage variant="general-info" />}
        footer={<Footer variant="estate-planning" />}
      >
        {children}
      </Layout>
    )

  const MemoizedConditionalLayout = useMemo(() => ConditionalLayout, [isInside, isLogged])

  return (
    <MemoizedConditionalLayout>
      <PageMeta description={article?.description} />
      {router.isFallback || article == null ? (
        <Block>
          <div>
            <PageTitle title={t('common.loading')} />
            <BlockSpinner />
          </div>
        </Block>
      ) : (
        <Fragment>
          <PageTitle title={article.title} />
          <PageMeta description={article.description} image={article.coverImage?.url} />
          <Head>
            <meta key="og:type" property="og:type" content="article" />
          </Head>

          <ArticleContent
            article={article}
            isInside={!!isInside}
            otherArticles={isInMenu ? [] : articleList.data?.flat()}
          />

          {isInMenu && <ArticleContentTable articlesMenu={articlesMenu} />}
        </Fragment>
      )}
    </MemoizedConditionalLayout>
  )
}

export const getStaticPaths: GetStaticPaths = async () => {
  const ids = await getArticleIds()

  return {
    paths: ids.map<{ params: { articleSlug: string } }>((item) => ({
      params: { articleSlug: item.slug },
    })),
    fallback: true,
  }
}

export const getStaticProps: GetStaticProps = async (ctx) => {
  const articleSlug = ctx.params?.articleSlug
  const article = typeof articleSlug === 'string' ? await getArticle(articleSlug) : null
  const articlesSetup = await contentful.getArticlesSetup()

  if (!article) {
    return { notFound: true, revalidate: REVALIDATE_SECS }
  }

  return {
    props: {
      ...(await getIntlProps(ctx)),
      article,
      articlesSetup: articlesSetup?.articlesSetupCollection?.items?.[0],
    },
    revalidate: REVALIDATE_SECS,
  }
}
